/**
 * PoolFahrzeugeSeite — Übersicht und Buchung von Poolfahrzeugen (Issue #164).
 *
 * Zwei Ansichten:
 *   • Karten-Ansicht: PoolFahrzeugCard pro Fahrzeug mit "Reservieren"-Button
 *   • Kalender-Ansicht: FahrzeugReservierungKalender für ein ausgewähltes Fahrzeug
 *
 * Reservierungs-Flow:
 *   1. „Reservieren"-Button → Reservierungs-Dialog öffnet sich
 *   2. Mitarbeiter gibt Zeitraum, Zweck ein
 *   3. UI prüft vorher: Nutzungsordnung bestätigt?
 *   4. POST an Supabase → bei Overlap klare Fehlermeldung
 *
 * Schlüsselausgabe-Flow (Admin only):
 *   - „Schlüssel ausgeben"-Button auf Karte → SchluesselausgabeDialog
 */

import { useState, useEffect, useCallback } from 'react';
import { Car, CalendarDays, LayoutGrid, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { DateTimePicker } from '@/components/ui/DateTimePicker';
import NutzungsordnungModal from '@/components/NutzungsordnungModal';
import { PoolFahrzeugCard, type PoolFahrzeug, type PoolReservierung } from '@/components/fahrzeuge/PoolFahrzeugCard';
import { FahrzeugReservierungKalender } from '@/components/fahrzeuge/FahrzeugReservierungKalender';
import SchluesselausgabeDialog from '@/components/fahrzeuge/SchluesselausgabeDialog';
import SchadenMeldenDialog from '@/components/fahrzeuge/SchadenMeldenDialog';

const KOOP_RED = '#C41E3A';

type ViewMode = 'karten' | 'kalender';

interface ReservierungsFormState {
  open: boolean;
  fahrzeugId: string;
  fahrzeugBezeichnung: string;
  beginn: string | null;
  ende: string | null;
  zweck: string;
  ziel: string;
  dienstlich: boolean;
}

const FORM_INITIAL: ReservierungsFormState = {
  open: false,
  fahrzeugId: '',
  fahrzeugBezeichnung: '',
  beginn: null,
  ende: null,
  zweck: '',
  ziel: '',
  dienstlich: true,
};

export default function PoolFahrzeugeSeite() {
  const { profile, isAdmin } = useAuth();
  const [viewMode, setViewMode] = useState<ViewMode>('karten');
  const [fahrzeuge, setFahrzeuge] = useState<PoolFahrzeug[]>([]);
  const [reservierungen, setReservierungen] = useState<PoolReservierung[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFahrzeugId, setSelectedFahrzeugId] = useState<string | null>(null);

  // Nutzungsordnung Gate
  const [nutzungsordnungOpen, setNutzungsordnungOpen] = useState(false);
  const [pendingFahrzeugId, setPendingFahrzeugId] = useState<string | null>(null);
  const [pendingSlot, setPendingSlot] = useState<{ beginn: Date; ende: Date } | null>(null);

  // Reservierungs-Dialog
  const [form, setForm] = useState<ReservierungsFormState>(FORM_INITIAL);
  const [saving, setSaving] = useState(false);

  // Schlüsselausgabe-Dialog
  const [schluesselDialogOpen, setSchluesselDialogOpen] = useState(false);
  const [schluesselReservierungId, setSchluesselReservierungId] = useState('');

  // Schaden-melden-Dialog
  const [schadenDialogReservierungId, setSchadenDialogReservierungId] = useState('');
  const [schadenDialogFahrzeugId, setSchadenDialogFahrzeugId] = useState('');
  const [schadenDialogFahrerMitarbeiterId, setSchadenDialogFahrerMitarbeiterId] = useState('');
  const [schadenDialogKennzeichen, setSchadenDialogKennzeichen] = useState('');
  const [schadenDialogMarke, setSchadenDialogMarke] = useState('');
  const [schadenDialogModell, setSchadenDialogModell] = useState('');
  const [schadenDialogOpen, setSchadenDialogOpen] = useState(false);

  const ladeFahrzeuge = useCallback(async () => {
    setLoading(true);
    try {
      const { data: fz, error: fzErr } = await supabase
        .from('fahrzeuge')
        .select('id, marke, modell, kennzeichen, bild_url, km_aktuell, nutzungsart, status')
        .eq('nutzungsart', 'pool')
        .order('marke');

      if (fzErr) { toast.error(`Fehler beim Laden: ${fzErr.message}`); return; }

      const ids = (fz ?? []).map((f: any) => f.id);
      if (ids.length === 0) { setFahrzeuge([]); return; }

      const vonTs = new Date();
      vonTs.setDate(vonTs.getDate() - 7);
      const bisTs = new Date();
      bisTs.setDate(bisTs.getDate() + 30);

      const { data: res, error: resErr } = await supabase
        .from('pool_reservierungen')
        .select(
          'id, fahrzeug_id, mitarbeiter_id, fahrer_mitarbeiter_id, ' +
          'beginn, ende, zweck, schluesselausgabe_am, storniert_am'
        )
        .in('fahrzeug_id', ids)
        .is('storniert_am', null)
        .not('beginn', 'is', null)
        .gte('ende', vonTs.toISOString())
        .lte('beginn', bisTs.toISOString())
        .order('beginn');

      if (resErr) { toast.error(`Reservierungen: ${resErr.message}`); }

      const resListe: PoolReservierung[] = (res ?? []).map((r: any) => ({
        id: r.id,
        fahrzeug_id: r.fahrzeug_id,
        mitarbeiter_id: r.mitarbeiter_id,
        fahrer_mitarbeiter_id: r.fahrer_mitarbeiter_id,
        beginn: r.beginn,
        ende: r.ende,
        zweck: r.zweck,
        schluesselausgabe_am: r.schluesselausgabe_am,
        storniert_am: r.storniert_am,
      }));

      setReservierungen(resListe);

      const now = Date.now();
      const fahrzeugeMitRes: PoolFahrzeug[] = (fz ?? []).map((f: any) => {
        const fahrzeugRes = resListe.filter((r) => r.fahrzeug_id === f.id);
        const aktuelleRes = fahrzeugRes.find(
          (r) => r.beginn && r.ende &&
            now >= new Date(r.beginn).getTime() &&
            now < new Date(r.ende).getTime(),
        ) ?? null;
        const naechsteRes = fahrzeugRes.find(
          (r) => r.beginn && new Date(r.beginn).getTime() > now,
        ) ?? null;
        const letzteAbgeschlosseneRes = fahrzeugRes
          .filter((r) => r.ende && new Date(r.ende).getTime() <= now && !r.storniert_am)
          .sort((a, b) => new Date(b.ende!).getTime() - new Date(a.ende!).getTime())[0] ?? null;
        return {
          ...f,
          aktuelle_reservierung: aktuelleRes,
          naechste_reservierung: naechsteRes,
          letzte_abgeschlossene_reservierung: aktuelleRes ? null : letzteAbgeschlosseneRes,
        } as PoolFahrzeug;
      });

      setFahrzeuge(fahrzeugeMitRes);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { ladeFahrzeuge(); }, [ladeFahrzeuge]);

  const handleReservierenClick = async (fahrzeugId: string, slotBeginn?: Date, slotEnde?: Date) => {
    if (!profile?.id) return;

    // Nutzungsordnung prüfen
    const { data: bestaetigung } = await supabase
      .from('nutzungsordnung_bestaetigungen')
      .select('id')
      .eq('fahrzeug_id', fahrzeugId)
      .eq('mitarbeiter_id', profile.id)
      .eq('version', '2026-01')
      .maybeSingle();

    if (!bestaetigung) {
      setPendingFahrzeugId(fahrzeugId);
      setPendingSlot(slotBeginn && slotEnde ? { beginn: slotBeginn, ende: slotEnde } : null);
      setNutzungsordnungOpen(true);
      return;
    }

    openReservierungsDialog(fahrzeugId, slotBeginn, slotEnde);
  };

  const openReservierungsDialog = (fahrzeugId: string, beginn?: Date, ende?: Date) => {
    const fz = fahrzeuge.find((f) => f.id === fahrzeugId);
    const defaultBeginn = beginn ?? (() => { const d = new Date(); d.setMinutes(0, 0, 0); d.setHours(d.getHours() + 1); return d; })();
    const defaultEnde = ende ?? (() => { const d = new Date(defaultBeginn); d.setHours(d.getHours() + 1); return d; })();

    setForm({
      open: true,
      fahrzeugId,
      fahrzeugBezeichnung: fz ? `${fz.marke} ${fz.modell}` : '',
      beginn: defaultBeginn.toISOString(),
      ende: defaultEnde.toISOString(),
      zweck: '',
      ziel: '',
      dienstlich: true,
    });
  };

  const handleNutzungsordnungBestaetigt = () => {
    setNutzungsordnungOpen(false);
    if (pendingFahrzeugId) {
      openReservierungsDialog(pendingFahrzeugId, pendingSlot?.beginn, pendingSlot?.ende);
      setPendingFahrzeugId(null);
      setPendingSlot(null);
    }
  };

  const handleSpeichernReservierung = async () => {
    if (!profile?.id) return;
    if (!form.beginn || !form.ende) { toast.error('Bitte Beginn und Ende angeben'); return; }
    if (!form.zweck.trim()) { toast.error('Bitte einen Zweck angeben'); return; }

    const beginnDt = new Date(form.beginn);
    const endeDt = new Date(form.ende);
    if (endeDt <= beginnDt) { toast.error('Ende muss nach Beginn liegen'); return; }

    // UI-Overlap-Vorprüfung
    const konflikt = reservierungen.find((r) => {
      if (r.fahrzeug_id !== form.fahrzeugId || r.storniert_am) return false;
      if (!r.beginn || !r.ende) return false;
      const rBeginn = new Date(r.beginn).getTime();
      const rEnde = new Date(r.ende).getTime();
      return beginnDt.getTime() < rEnde && endeDt.getTime() > rBeginn;
    });

    if (konflikt) {
      toast.error(
        `Das Fahrzeug ist in diesem Zeitraum bereits belegt (${
          konflikt.zweck ?? 'Reservierung'
        })`,
        { description: 'Bitte einen anderen Zeitraum wählen.' },
      );
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from('pool_reservierungen').insert({
        fahrzeug_id: form.fahrzeugId,
        mitarbeiter_id: profile.id,
        fahrer_mitarbeiter_id: profile.id,
        beginn: form.beginn,
        ende: form.ende,
        zweck: form.zweck.trim(),
        ziel: form.ziel.trim() || null,
        dienstlich: form.dienstlich,
        zuordnung_quelle: 'ui_buchung',
      });

      if (error) {
        if (error.code === '23P01' || error.message.includes('overlap')) {
          toast.error('Das Fahrzeug ist für diesen Zeitraum bereits belegt', {
            description: 'Ein anderer Mitarbeiter hat gerade den gleichen Zeitraum gebucht.',
          });
        } else {
          toast.error(`Reservierung fehlgeschlagen: ${error.message}`);
        }
        return;
      }

      toast.success('Reservierung gespeichert');
      setForm(FORM_INITIAL);
      await ladeFahrzeuge();
    } finally {
      setSaving(false);
    }
  };

  const handleSchluesselAusgabe = (reservierungId: string) => {
    setSchluesselReservierungId(reservierungId);
    setSchluesselDialogOpen(true);
  };

  const handleSchadenMelden = (reservierungId: string) => {
    const fz = fahrzeuge.find((f) =>
      f.aktuelle_reservierung?.id === reservierungId ||
      f.letzte_abgeschlossene_reservierung?.id === reservierungId,
    );
    if (!fz) return;
    const res = fz.aktuelle_reservierung?.id === reservierungId
      ? fz.aktuelle_reservierung
      : fz.letzte_abgeschlossene_reservierung;
    const fahrerMitarbeiterId =
      res?.fahrer_mitarbeiter_id ?? res?.mitarbeiter_id ?? profile?.id ?? '';
    setSchadenDialogReservierungId(reservierungId);
    setSchadenDialogFahrzeugId(fz.id);
    setSchadenDialogFahrerMitarbeiterId(fahrerMitarbeiterId);
    setSchadenDialogKennzeichen(fz.kennzeichen ?? '');
    setSchadenDialogMarke(fz.marke);
    setSchadenDialogModell(fz.modell);
    setSchadenDialogOpen(true);
  };

  const handleSlotClick = (fahrzeugId: string, beginn: Date, ende: Date) => {
    handleReservierenClick(fahrzeugId, beginn, ende);
  };

  const kalenderFahrzeug = fahrzeuge.find((f) => f.id === selectedFahrzeugId) ?? fahrzeuge[0] ?? null;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Car className="size-5" style={{ color: KOOP_RED }} />
          <h1 className="text-xl font-bold text-gray-900">Poolfahrzeuge</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={viewMode === 'karten' ? 'default' : 'outline'}
            onClick={() => setViewMode('karten')}
          >
            <LayoutGrid className="size-3.5 mr-1" />
            Karten
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'kalender' ? 'default' : 'outline'}
            onClick={() => setViewMode('kalender')}
          >
            <CalendarDays className="size-3.5 mr-1" />
            Kalender
          </Button>
          <Button size="icon" variant="ghost" onClick={ladeFahrzeuge} disabled={loading} aria-label="Neu laden">
            <RefreshCw className={`size-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-12">
          <Loader2 className="size-6 animate-spin text-gray-400" />
        </div>
      )}

      {!loading && fahrzeuge.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Car className="size-10 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">Keine Poolfahrzeuge vorhanden.</p>
          <p className="text-xs mt-1 text-gray-400">Fahrzeuge mit Nutzungsart „pool" erscheinen hier.</p>
        </div>
      )}

      {/* Karten-Ansicht */}
      {!loading && viewMode === 'karten' && fahrzeuge.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {fahrzeuge.map((fz) => (
            <PoolFahrzeugCard
              key={fz.id}
              fahrzeug={fz}
              onReservieren={handleReservierenClick}
              onSchluesselAusgabe={handleSchluesselAusgabe}
              onSchadenMelden={handleSchadenMelden}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      )}

      {/* Kalender-Ansicht */}
      {!loading && viewMode === 'kalender' && fahrzeuge.length > 0 && (
        <div className="space-y-4">
          {/* Fahrzeug-Auswahl */}
          {fahrzeuge.length > 1 && (
            <div className="flex gap-2 flex-wrap">
              {fahrzeuge.map((fz) => (
                <Button
                  key={fz.id}
                  size="sm"
                  variant={selectedFahrzeugId === fz.id || (!selectedFahrzeugId && fz === fahrzeuge[0]) ? 'default' : 'outline'}
                  onClick={() => setSelectedFahrzeugId(fz.id)}
                >
                  {fz.marke} {fz.modell}
                  {fz.kennzeichen && <span className="ml-1 text-xs opacity-70">{fz.kennzeichen}</span>}
                </Button>
              ))}
            </div>
          )}

          {kalenderFahrzeug && (
            <>
              <p className="text-sm text-gray-600">
                Klick auf einen freien Slot um eine Reservierung für{' '}
                <strong>{kalenderFahrzeug.marke} {kalenderFahrzeug.modell}</strong> anzulegen.
              </p>
              <FahrzeugReservierungKalender
                fahrzeugId={kalenderFahrzeug.id}
                reservierungen={reservierungen.filter((r) => r.fahrzeug_id === kalenderFahrzeug.id)}
                onSlotClick={(beginn, ende) => handleSlotClick(kalenderFahrzeug.id, beginn, ende)}
              />
            </>
          )}
        </div>
      )}

      {/* Nutzungsordnung Modal */}
      {nutzungsordnungOpen && pendingFahrzeugId && profile && (
        <NutzungsordnungModal
          open={nutzungsordnungOpen}
          mitarbeiterId={profile.id}
          fahrzeugId={pendingFahrzeugId}
          fahrzeugBezeichnung={
            fahrzeuge.find((f) => f.id === pendingFahrzeugId)
              ? `${fahrzeuge.find((f) => f.id === pendingFahrzeugId)!.marke} ${fahrzeuge.find((f) => f.id === pendingFahrzeugId)!.modell}`
              : undefined
          }
          onConfirmed={handleNutzungsordnungBestaetigt}
          onCancel={() => { setNutzungsordnungOpen(false); setPendingFahrzeugId(null); }}
        />
      )}

      {/* Reservierungs-Dialog */}
      {form.open && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="reservierung-dialog"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          <div className="absolute inset-0 bg-black/40" onClick={() => !saving && setForm(FORM_INITIAL)} aria-hidden="true" />
          <div className="relative z-10 bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-base font-semibold">
              Reservierung: {form.fahrzeugBezeichnung}
            </h2>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Beginn</Label>
                <div data-testid="reservierung-beginn">
                  <DateTimePicker
                    value={form.beginn}
                    onChange={(v) => setForm((f) => ({ ...f, beginn: v }))}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Ende</Label>
                <div data-testid="reservierung-ende">
                  <DateTimePicker
                    value={form.ende}
                    onChange={(v) => setForm((f) => ({ ...f, ende: v }))}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="res-zweck">Zweck *</Label>
              <Input
                id="res-zweck"
                placeholder="z.B. Kundentermin, Besichtigung, Werkstatt…"
                value={form.zweck}
                onChange={(e) => setForm((f) => ({ ...f, zweck: e.target.value }))}
                maxLength={200}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="res-ziel">Ziel (optional)</Label>
              <Input
                id="res-ziel"
                placeholder="z.B. Düsseldorf, Kundennr. 4711…"
                value={form.ziel}
                onChange={(e) => setForm((f) => ({ ...f, ziel: e.target.value }))}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                id="res-dienstlich"
                type="checkbox"
                checked={form.dienstlich}
                onChange={(e) => setForm((f) => ({ ...f, dienstlich: e.target.checked }))}
                className="size-4 rounded"
              />
              <Label htmlFor="res-dienstlich" className="cursor-pointer">Dienstliche Fahrt</Label>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="ghost" size="sm" onClick={() => setForm(FORM_INITIAL)} disabled={saving}>
                Abbrechen
              </Button>
              <Button
                size="sm"
                data-testid="reservierung-speichern"
                onClick={handleSpeichernReservierung}
                disabled={saving || !form.zweck.trim() || !form.beginn || !form.ende}
                style={{ backgroundColor: KOOP_RED, color: 'white', border: 'none' }}
              >
                {saving ? <><Loader2 className="size-3.5 animate-spin mr-1.5" />Speichern…</> : 'Reservierung speichern'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Schlüsselausgabe Dialog */}
      {schluesselDialogOpen && profile && (
        <SchluesselausgabeDialog
          open={schluesselDialogOpen}
          reservierungId={schluesselReservierungId}
          ausgebendeMitarbeiterId={profile.id}
          onClose={() => setSchluesselDialogOpen(false)}
          onCompleted={() => {
            setSchluesselDialogOpen(false);
            ladeFahrzeuge();
          }}
        />
      )}

      {/* Schaden-melden Dialog */}
      {schadenDialogOpen && (
        <SchadenMeldenDialog
          open={schadenDialogOpen}
          reservierungId={schadenDialogReservierungId}
          fahrzeugId={schadenDialogFahrzeugId}
          fahrerMitarbeiterId={schadenDialogFahrerMitarbeiterId}
          kennzeichen={schadenDialogKennzeichen}
          fahrzeugMarke={schadenDialogMarke}
          fahrzeugModell={schadenDialogModell}
          onClose={() => setSchadenDialogOpen(false)}
        />
      )}
    </div>
  );
}
